// AdminTab.jsx
// TrapperKeeper Project Administration
// Phase 7: Project onboarding wizard with guided setup
// Access controlled via ADMIN_ACL in server .env

import { useState, useEffect, useCallback, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import { makeAuthFetch } from "./apiAuth";
import { useOrgDirectory } from "./OrgDirectoryContext";
import TabConfigEditor from "./TabConfigEditor";

const C = {
  dark: "#1a1a2e", gold: "#c9a84c", white: "#ffffff",
  border: "#e5e7eb", muted: "#6b7280", text: "#374151",
  green: "#27ae60", orange: "#e67e22", red: "#c0392b",
  blue: "#2980b9", light: "#f8f7f4",
};

const GRAPH_BASE = "https://graph.microsoft.com/v1.0";

export const ALL_TABS = [
  { name: "Status",                     category: "core",    defaultOn: true,  desc: "Live status tracker from SharePoint" },
  { name: "AI Summit",                  category: "summit",  defaultOn: true,  desc: "Summit agenda and session overview" },
  { name: "AI Charter",                 category: "docs",    defaultOn: true,  desc: "AI program charter document" },
  { name: "AI Business Case",           category: "docs",    defaultOn: true,  desc: "Business case and ROI analysis" },
  { name: "Data Audit",                 category: "data",    defaultOn: true,  desc: "Data source audit and trust levels" },
  { name: "Architecture",               category: "data",    defaultOn: true,  desc: "Technical architecture overview" },
  { name: "AI Strategy",                category: "docs",    defaultOn: true,  desc: "AI strategy framework" },
  { name: "Agentic Day Planner",        category: "pilot",   defaultOn: true,  desc: "Pilot program tracker" },
  { name: "Ownership",                  category: "docs",    defaultOn: true,  desc: "Ownership and accountability matrix" },
  { name: "Metrics",                    category: "data",    defaultOn: true,  desc: "KPI and metrics definitions" },
  { name: "Readiness Capture",          category: "capture", defaultOn: true,  desc: "AI readiness assessment capture" },
  { name: "Use Case Capture",           category: "capture", defaultOn: true,  desc: "Use case prioritization capture" },
  { name: "Current State Capture",      category: "capture", defaultOn: true,  desc: "Current state assessment capture" },
  { name: "Data Platform Capture",      category: "capture", defaultOn: true,  desc: "Data platform decisions capture" },
  { name: "Pilot Planning Capture",     category: "capture", defaultOn: true,  desc: "Pilot planning capture" },
  { name: "Governance Capture",         category: "capture", defaultOn: true,  desc: "AI governance framework capture" },
  { name: "Story Capture",              category: "capture", defaultOn: true,  desc: "Story framework capture" },
  { name: "Execution Capture",          category: "capture", defaultOn: true,  desc: "Execution planning capture" },
  { name: "Day 1 Wrap Capture",         category: "capture", defaultOn: true,  desc: "Day 1 decisions log" },
  { name: "Summit Close Capture",       category: "capture", defaultOn: true,  desc: "Final commitments and action items" },
  { name: "New Hire Onboarding Tracker",category: "hr",      defaultOn: false, desc: "New hire onboarding tracker" },
  { name: "SMS Notifications",          category: "core",    defaultOn: true,  desc: "Twilio SMS consent capture (compliance-required)" },
  { name: "Admin",                      category: "admin",   defaultOn: true,  desc: "Platform administration (admin only)" },
];

// Tabs pre-checked for every new project created via the wizard below.
// NOT derived from ALL_TABS' defaultOn field -- that field exists as
// per-tab documentation/intent but nothing reads it to compute this list;
// this array is the actual mechanism controlling wizard defaults. Status
// and Admin are the pre-existing baseline; SMS Notifications must always
// be here too -- it is Twilio A2P 10DLC compliance-required, never
// something a project owner should have to remember to opt into.
export const DEFAULT_ENABLED_TABS = ["Status", "SMS Notifications", "Admin"];

const PERMISSION_LEVELS = ["view", "write", "modify", "admin"];
const LEVEL_COLORS = { view: C.muted, write: C.blue, modify: C.orange, admin: C.red };
const LEVEL_DESC = {
  view:   "Read only - can view all tabs",
  write:  "Can write and capture data",
  modify: "Can modify existing data",
  admin:  "Full access including Admin tab",
};

const WIZARD_STEPS = [
  { id: "basics",      label: "Project",     icon: "📋" },
  { id: "sharepoint",  label: "SharePoint",  icon: "📊" },
  { id: "team",        label: "Team",        icon: "👥" },
  { id: "tabs",        label: "Tabs",        icon: "🗂" },
  { id: "review",      label: "Review",      icon: "✅" },
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function Card({ children, style }) {
  return (
    <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 8, padding: 16, marginBottom: 12, ...style }}>
      {children}
    </div>
  );
}

function Badge({ label, color }) {
  const colors = {
    active:   { bg: "#d1fae5", text: "#065f46" },
    archived: { bg: "#f3f4f6", text: "#6b7280" },
    draft:    { bg: "#fef3c7", text: "#92400e" },
  };
  const c = colors[color] || colors.active;
  return (
    <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: c.bg, color: c.text, fontWeight: 700, textTransform: "uppercase" }}>
      {label}
    </span>
  );
}

function Field({ label, children, hint }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: C.dark, marginBottom: 3 }}>{label}</div>
      {hint && <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>{hint}</div>}
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, style }) {
  return (
    <input value={value} onChange={onChange} placeholder={placeholder}
      style={{ width: "100%", padding: "8px 10px", border: "1px solid " + C.border, borderRadius: 6, fontSize: 13, boxSizing: "border-box", ...style }} />
  );
}

function StepIndicator({ currentStep }) {
  const currentIdx = WIZARD_STEPS.findIndex(function(s) { return s.id === currentStep; });
  return (
    <div style={{ display: "flex", alignItems: "center", marginBottom: 24 }}>
      {WIZARD_STEPS.map(function(step, i) {
        const done = i < currentIdx;
        const active = i === currentIdx;
        return (
          <div key={step.id} style={{ display: "flex", alignItems: "center", flex: i < WIZARD_STEPS.length - 1 ? 1 : 0 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: done ? C.green : active ? C.dark : "#f3f4f6", color: done ? C.white : active ? C.gold : C.muted, display: "flex", alignItems: "center", justifyContent: "center", fontSize: done ? 14 : 16, fontWeight: 700, border: active ? "2px solid " + C.gold : "none" }}>
                {done ? "✓" : step.icon}
              </div>
              <div style={{ fontSize: 10, color: active ? C.dark : C.muted, fontWeight: active ? 700 : 400, whiteSpace: "nowrap" }}>
                {step.label}
              </div>
            </div>
            {i < WIZARD_STEPS.length - 1 && (
              <div style={{ flex: 1, height: 2, background: done ? C.green : C.border, margin: "0 6px", marginBottom: 16 }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── WIZARD STEPS ─────────────────────────────────────────────────────────────

function StepBasics({ data, setData }) {
  return (
    <div>
      <div style={{ fontSize: 16, fontWeight: 700, color: C.dark, marginBottom: 4 }}>Project Details</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>Basic information about the project and client.</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label="Project Name *" hint="Descriptive name for this engagement">
          <Input value={data.name} onChange={function(e) { setData({ ...data, name: e.target.value }); }} placeholder="e.g. ACME Data Platform" />
        </Field>
        <Field label="Client *" hint="Company or organization name">
          <Input value={data.client} onChange={function(e) { setData({ ...data, client: e.target.value }); }} placeholder="e.g. ACME Corp" />
        </Field>
        <Field label="Subtitle" hint="Phase, timeline, or brief description">
          <Input value={data.subtitle} onChange={function(e) { setData({ ...data, subtitle: e.target.value }); }} placeholder="e.g. Phase 1 - AI Foundation" />
        </Field>
        <Field label="Project URL" hint="Where this project will be accessible">
          <Input value={data.domain} onChange={function(e) { setData({ ...data, domain: e.target.value }); }} placeholder="e.g. https://datamvps.tripleco.com/acme" />
        </Field>
      </div>
    </div>
  );
}

function StepSharePoint({ data, setData, accessToken }) {
  const [searching, setSearching] = useState(false);
  const [files, setFiles] = useState([]);
  const [fileFilter, setFileFilter] = useState("");
  const [searchError, setSearchError] = useState(null);
  const [searched, setSearched] = useState(false);

  async function findFiles() {
    if (!data.spTenant || !data.spSite) {
      setSearchError("Enter tenant and site name first.");
      return;
    }
    setSearching(true);
    setSearchError(null);
    setFiles([]);
    try {
      // Get site ID
      const siteRes = await fetch(
        GRAPH_BASE + "/sites/" + data.spTenant + ":/sites/" + data.spSite,
        { headers: { Authorization: "Bearer " + accessToken } }
      );
      if (!siteRes.ok) throw new Error("Site not found. Check tenant and site name.");
      const siteData = await siteRes.json();

      // Get drives
      const drivesRes = await fetch(
        GRAPH_BASE + "/sites/" + siteData.id + "/drives",
        { headers: { Authorization: "Bearer " + accessToken } }
      );
      const drivesData = await drivesRes.json();
      const drives = drivesData.value || [];

      // Search for xlsx files across all drives
      const allFiles = [];
      for (const drive of drives) {
        try {
          const searchRes = await fetch(
            GRAPH_BASE + "/drives/" + drive.id + "/root/search(q='.xlsx')?$select=id,name,parentReference,size,lastModifiedDateTime",
            { headers: { Authorization: "Bearer " + accessToken } }
          );
          const searchData = await searchRes.json();
          for (const file of (searchData.value || [])) {
            allFiles.push({ ...file, driveId: drive.id, driveName: drive.name });
          }
        } catch (e) { /* skip drive */ }
      }
      setFiles(allFiles);
      setSearched(true);
      if (allFiles.length === 0) setSearchError("No .xlsx files found. Check site name or add the status file to SharePoint first.");
    } catch (e) {
      setSearchError(e.message);
    } finally {
      setSearching(false);
    }
  }

  function selectFile(file) {
    setData({
      ...data,
      spDriveId: file.driveId,
      spFileId: file.id,
      spFileName: file.name,
    });
  }

  return (
    <div>
      <div style={{ fontSize: 16, fontWeight: 700, color: C.dark, marginBottom: 4 }}>SharePoint Connection</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>Connect to the project status file in SharePoint. Enter the tenant and site, then find your file.</div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <Field label="SharePoint Tenant" hint="e.g. triplecc.sharepoint.com">
          <Input value={data.spTenant} onChange={function(e) { setData({ ...data, spTenant: e.target.value }); }} placeholder="triplecc.sharepoint.com" />
        </Field>
        <Field label="Site Name" hint="The SharePoint site where the file lives">
          <Input value={data.spSite} onChange={function(e) { setData({ ...data, spSite: e.target.value }); }} placeholder="e.g. OperationsITHub" />
        </Field>
      </div>

      <button onClick={findFiles} disabled={searching || !data.spTenant || !data.spSite}
        style={{ padding: "9px 20px", background: searching ? "#9ca3af" : C.dark, color: C.gold, border: "none", borderRadius: 6, cursor: searching ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, marginBottom: 16 }}>
        {searching ? "Searching..." : "🔍 Find Excel Files in SharePoint"}
      </button>

      {searchError && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: C.red }}>
          {searchError}
        </div>
      )}

      {data.spFileName && (
        <div style={{ background: "#d1fae5", border: "1px solid #6ee7b7", borderRadius: 6, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: "#065f46", fontWeight: 600 }}>
          Selected: {data.spFileName}
        </div>
      )}

      {files.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
            {files.length} file{files.length !== 1 ? "s" : ""} found - select your status file
          </div>
          {files.map(function(file) {
            const selected = data.spFileId === file.id;
            return (
              <div key={file.id} onClick={function() { selectFile(file); }}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", border: "1px solid " + (selected ? C.green : C.border), borderRadius: 6, marginBottom: 6, cursor: "pointer", background: selected ? "#f0fdf4" : C.white }}>
                <span style={{ fontSize: 20 }}>📊</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: selected ? 700 : 400, color: C.dark }}>{file.name}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{file.driveName} · {file.parentReference?.path?.split("root:")[1] || "/"}</div>
                </div>
                <div style={{ fontSize: 11, color: C.muted }}>
                  {new Date(file.lastModifiedDateTime).toLocaleDateString()}
                </div>
                {selected && <span style={{ color: C.green, fontWeight: 700 }}>✓</span>}
              </div>
            );
          })}
        </div>
      )}

      {searched && files.length === 0 && !searchError && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.dark, marginBottom: 8 }}>Enter IDs manually</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Drive ID">
              <Input value={data.spDriveId} onChange={function(e) { setData({ ...data, spDriveId: e.target.value }); }} placeholder="b!lQhae..." />
            </Field>
            <Field label="File ID">
              <Input value={data.spFileId} onChange={function(e) { setData({ ...data, spFileId: e.target.value }); }} placeholder="0137WX5P..." />
            </Field>
          </div>
        </div>
      )}
    </div>
  );
}

function StepTeam({ data, setData }) {
  function addMember() {
    setData({ ...data, team: [...data.team, { name: "", email: "", level: "write" }] });
  }

  function updateMember(i, field, val) {
    const team = data.team.map(function(m, idx) { return idx === i ? { ...m, [field]: val } : m; });
    setData({ ...data, team });
  }

  function removeMember(i) {
    setData({ ...data, team: data.team.filter(function(_, idx) { return idx !== i; }) });
  }

  return (
    <div>
      <div style={{ fontSize: 16, fontWeight: 700, color: C.dark, marginBottom: 4 }}>Team Members</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>Add team members and set their permission level. You can change this later.</div>

      <div style={{ background: "#f8f7f4", border: "1px solid " + C.border, borderRadius: 6, padding: "10px 14px", marginBottom: 16, fontSize: 11, color: C.muted }}>
        Permission levels: <strong style={{ color: C.muted }}>view</strong> (read only) · <strong style={{ color: C.blue }}>write</strong> (capture data) · <strong style={{ color: C.orange }}>modify</strong> (edit data) · <strong style={{ color: C.red }}>admin</strong> (full access)
      </div>

      {data.team.map(function(member, i) {
        return (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 120px 32px", gap: 8, marginBottom: 8, alignItems: "center" }}>
            <input value={member.name} onChange={function(e) { updateMember(i, "name", e.target.value); }}
              placeholder="Full name"
              style={{ padding: "8px 10px", border: "1px solid " + C.border, borderRadius: 6, fontSize: 13 }} />
            <input value={member.email} onChange={function(e) { updateMember(i, "email", e.target.value); }}
              placeholder="email@domain.com"
              style={{ padding: "8px 10px", border: "1px solid " + C.border, borderRadius: 6, fontSize: 13 }} />
            <select value={member.level} onChange={function(e) { updateMember(i, "level", e.target.value); }}
              style={{ padding: "8px 10px", border: "1px solid " + (LEVEL_COLORS[member.level] || C.border), borderRadius: 6, fontSize: 12, color: LEVEL_COLORS[member.level] || C.dark, fontWeight: 600 }}>
              {PERMISSION_LEVELS.map(function(l) { return <option key={l} value={l}>{l}</option>; })}
            </select>
            <button onClick={function() { removeMember(i); }}
              style={{ border: "1px solid " + C.border, borderRadius: 6, background: C.white, color: C.red, cursor: "pointer", fontSize: 14, padding: "8px" }}>✕</button>
          </div>
        );
      })}

      <button onClick={addMember}
        style={{ padding: "8px 16px", border: "1px solid " + C.border, borderRadius: 6, background: C.white, color: C.dark, cursor: "pointer", fontSize: 12, marginTop: 4 }}>
        + Add team member
      </button>

      <div style={{ marginTop: 20, padding: "12px 14px", background: "#f8f7f4", border: "1px solid " + C.border, borderRadius: 6, fontSize: 11, color: C.muted }}>
        Client guests: invite them to your Azure AD tenant first, then add them here with <strong>view</strong> level. They will see only this project.
      </div>
    </div>
  );
}

function StepTabs({ data, setData }) {
  const categories = [...new Set(ALL_TABS.map(function(t) { return t.category; }))];

  function toggleTab(name) {
    const enabled = data.enabledTabs.includes(name);
    setData({
      ...data,
      enabledTabs: enabled
        ? data.enabledTabs.filter(function(t) { return t !== name; })
        : [...data.enabledTabs, name],
    });
  }

  function toggleCategory(cat) {
    const catTabs = ALL_TABS.filter(function(t) { return t.category === cat; }).map(function(t) { return t.name; });
    const allEnabled = catTabs.every(function(t) { return data.enabledTabs.includes(t); });
    setData({
      ...data,
      enabledTabs: allEnabled
        ? data.enabledTabs.filter(function(t) { return !catTabs.includes(t); })
        : [...new Set([...data.enabledTabs, ...catTabs])],
    });
  }

  const categoryLabels = {
    core: "Core", summit: "Summit", docs: "Documents",
    data: "Data", capture: "Capture Tabs", pilot: "Pilot",
    hr: "HR", admin: "Admin",
  };

  return (
    <div>
      <div style={{ fontSize: 16, fontWeight: 700, color: C.dark, marginBottom: 4 }}>Enabled Tabs</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>
        Choose which tabs are active for this project. {data.enabledTabs.length} of {ALL_TABS.length} tabs enabled.
      </div>

      {categories.map(function(cat) {
        const catTabs = ALL_TABS.filter(function(t) { return t.category === cat; });
        const allEnabled = catTabs.every(function(t) { return data.enabledTabs.includes(t.name); });
        const someEnabled = catTabs.some(function(t) { return data.enabledTabs.includes(t.name); });
        return (
          <div key={cat} style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <input type="checkbox" checked={allEnabled} ref={function(el) { if (el) el.indeterminate = someEnabled && !allEnabled; }}
                onChange={function() { toggleCategory(cat); }} style={{ cursor: "pointer" }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: C.dark, textTransform: "uppercase", letterSpacing: 0.5 }}>
                {categoryLabels[cat] || cat}
              </span>
              <span style={{ fontSize: 11, color: C.muted }}>
                ({catTabs.filter(function(t) { return data.enabledTabs.includes(t.name); }).length}/{catTabs.length})
              </span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, paddingLeft: 24 }}>
              {catTabs.map(function(tab) {
                const enabled = data.enabledTabs.includes(tab.name);
                return (
                  <div key={tab.name} onClick={function() { toggleTab(tab.name); }}
                    style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid " + (enabled ? C.dark : C.border), background: enabled ? C.dark : C.white, color: enabled ? C.gold : C.muted, fontSize: 12, cursor: "pointer", fontWeight: enabled ? 600 : 400, userSelect: "none" }}
                    title={tab.desc}>
                    {tab.name}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StepReview({ data }) {
  const validTeam = data.team.filter(function(m) { return m.name && m.email; });
  return (
    <div>
      <div style={{ fontSize: 16, fontWeight: 700, color: C.dark, marginBottom: 4 }}>Review and Create</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>Confirm project details before creating.</div>

      <Card>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.dark, marginBottom: 12 }}>Project</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[["Name", data.name], ["Client", data.client], ["Subtitle", data.subtitle || "—"], ["URL", data.domain || "—"]].map(function([label, val]) {
            return (
              <div key={label}>
                <div style={{ fontSize: 10, color: C.muted }}>{label}</div>
                <div style={{ fontSize: 13, color: C.dark, fontWeight: 600 }}>{val}</div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.dark, marginBottom: 12 }}>SharePoint</div>
        {data.spFileName ? (
          <div style={{ fontSize: 13, color: C.green, fontWeight: 600 }}>✓ {data.spFileName}</div>
        ) : data.spDriveId ? (
          <div style={{ fontSize: 12, color: C.muted }}>Manual IDs provided</div>
        ) : (
          <div style={{ fontSize: 12, color: C.orange }}>⚠ No file selected - Status tab will not load live data</div>
        )}
        {data.spTenant && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{data.spTenant} / {data.spSite}</div>}
        {data.spDriveId && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Drive ID and File ID captured. Status tab activation requires platformService update for this project.</div>}
      </Card>

      <Card>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.dark, marginBottom: 12 }}>Team ({validTeam.length} members)</div>
        {validTeam.length === 0 ? (
          <div style={{ fontSize: 12, color: C.muted }}>No team members added</div>
        ) : (
          validTeam.map(function(m) {
            return (
              <div key={m.email} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <div>
                  <span style={{ fontSize: 13, color: C.dark }}>{m.name}</span>
                  <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>{m.email}</span>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: LEVEL_COLORS[m.level] || C.muted }}>{m.level}</span>
              </div>
            );
          })
        )}
      </Card>

      <Card>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.dark, marginBottom: 12 }}>Tabs ({data.enabledTabs.length} enabled)</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {data.enabledTabs.map(function(tab) {
            return (
              <span key={tab} style={{ padding: "3px 10px", borderRadius: 12, background: "#f3f4f6", color: C.muted, fontSize: 11 }}>{tab}</span>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// ─── PROJECT WIZARD ───────────────────────────────────────────────────────────

function ProjectWizard({ onSave, onCancel }) {
  const { instance, accounts } = useMsal();
  const [step, setStep] = useState("basics");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [accessToken, setAccessToken] = useState(null);

  const [data, setData] = useState({
    name: "", client: "", subtitle: "", domain: "",
    spTenant: "triplecc.sharepoint.com", spSite: "",
    spDriveId: "", spFileId: "", spFileName: "",
    team: [{ name: "", email: "", level: "write" }],
    enabledTabs: DEFAULT_ENABLED_TABS,
  });

  useEffect(function() {
    if (accounts[0]) {
      instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] })
        .then(function(r) { setAccessToken(r.accessToken); })
        .catch(function() {});
    }
  }, [instance, accounts]);

  const stepIdx = WIZARD_STEPS.findIndex(function(s) { return s.id === step; });
  const isFirst = stepIdx === 0;
  const isLast = stepIdx === WIZARD_STEPS.length - 1;

  function canAdvance() {
    if (step === "basics") return data.name && data.client;
    return true;
  }

  function next() {
    if (!canAdvance()) { setError("Please fill in the required fields."); return; }
    setError(null);
    setStep(WIZARD_STEPS[stepIdx + 1].id);
  }

  function back() {
    setError(null);
    setStep(WIZARD_STEPS[stepIdx - 1].id);
  }

  async function handleCreate() {
    setSaving(true);
    setError(null);
    try {
      const validTeam = data.team.filter(function(m) { return m.name && m.email; });
      const team = {};
      const userOverrides = {};
      validTeam.forEach(function(m) {
        team[m.name] = m.email;
        userOverrides[m.email.toLowerCase()] = m.level;
      });

      const payload = {
        name: data.name,
        client: data.client,
        subtitle: data.subtitle,
        domain: data.domain,
        sharepoint: {
          tenant: data.spTenant,
          site: data.spSite,
          driveId: data.spDriveId,
          statusFileId: data.spFileId,
          statusFileName: data.spFileName,
          summitDataFolder: "",
        },
        team,
        tabs: {
          public: data.enabledTabs.includes("Status") ? ["Status"] : [],
          authenticated: data.enabledTabs.filter(function(t) { return t !== "Status" && t !== "Admin"; }),
          admin: data.enabledTabs.includes("Admin") ? ["Admin"] : [],
        },
        permissions: {
          groups: {},
          userOverrides,
          defaultLevel: "write",
        },
      };

      const res = await makeAuthFetch(instance, accounts[0])("/api/projects", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Failed to create project");
      onSave(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ maxWidth: 700, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={onCancel}
          style={{ padding: "6px 12px", border: "1px solid " + C.border, borderRadius: 6, background: C.white, color: C.muted, cursor: "pointer", fontSize: 12 }}>
          ✕ Cancel
        </button>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.dark }}>New Project</div>
      </div>

      <StepIndicator currentStep={step} />

      <Card style={{ minHeight: 300 }}>
        {step === "basics"     && <StepBasics data={data} setData={setData} />}
        {step === "sharepoint" && <StepSharePoint data={data} setData={setData} accessToken={accessToken} />}
        {step === "team"       && <StepTeam data={data} setData={setData} />}
        {step === "tabs"       && <StepTabs data={data} setData={setData} />}
        {step === "review"     && <StepReview data={data} />}
      </Card>

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: C.red }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button onClick={back} disabled={isFirst}
          style={{ padding: "9px 20px", border: "1px solid " + C.border, borderRadius: 6, background: C.white, color: isFirst ? C.muted : C.dark, cursor: isFirst ? "not-allowed" : "pointer", fontSize: 13 }}>
          ← Back
        </button>
        <div style={{ fontSize: 11, color: C.muted }}>
          Step {stepIdx + 1} of {WIZARD_STEPS.length}
        </div>
        {isLast ? (
          <button onClick={handleCreate} disabled={saving}
            style={{ padding: "9px 24px", background: saving ? "#9ca3af" : C.green, color: C.white, border: "none", borderRadius: 6, cursor: saving ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700 }}>
            {saving ? "Creating..." : "Create Project"}
          </button>
        ) : (
          <button onClick={next} disabled={!canAdvance()}
            style={{ padding: "9px 20px", background: canAdvance() ? C.dark : "#9ca3af", color: C.gold, border: "none", borderRadius: 6, cursor: canAdvance() ? "pointer" : "not-allowed", fontSize: 13, fontWeight: 700 }}>
            Next →
          </button>
        )}
      </div>
    </div>
  );
}

// ─── SHAREPOINT EDITOR ────────────────────────────────────────────────────────

function SharePointEditor({ project, onSave }) {
  const { instance, accounts } = useMsal();
  const [tenant, setTenant] = useState(project.sharepoint?.tenant || "triplecc.sharepoint.com");
  const [site, setSite] = useState(project.sharepoint?.site || "");
  const [searching, setSearching] = useState(false);
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const [fileFilter, setFileFilter] = useState("");
  const [manualDriveId, setManualDriveId] = useState(project.sharepoint?.driveId || "");
  const [manualFileId, setManualFileId] = useState(project.sharepoint?.statusFileId || "");

  async function findFiles() {
    if (!tenant || !site) { setSearchError("Enter tenant and site first."); return; }
    setSearching(true); setSearchError(null); setFiles([]);
    try {
      const tokenResp = await instance.acquireTokenSilent({ scopes: ["Files.Read.All", "Sites.Read.All"], account: accounts[0] });
      const token = tokenResp.accessToken;
      const siteRes = await fetch("https://graph.microsoft.com/v1.0/sites/" + tenant + ":/sites/" + site, { headers: { Authorization: "Bearer " + token } });
      if (!siteRes.ok) throw new Error("Site not found");
      const siteData = await siteRes.json();
      const drivesRes = await fetch("https://graph.microsoft.com/v1.0/sites/" + siteData.id + "/drives", { headers: { Authorization: "Bearer " + token } });
      const drivesData = await drivesRes.json();
      const allFiles = [];
      for (const drive of (drivesData.value || [])) {
        try {
          const searchRes = await fetch("https://graph.microsoft.com/v1.0/drives/" + drive.id + "/root/search(q='.xlsx')?$select=id,name,parentReference,lastModifiedDateTime&$top=100", { headers: { Authorization: "Bearer " + token } });
          const searchData = await searchRes.json();
          for (const file of (searchData.value || [])) { allFiles.push({ ...file, driveId: drive.id, driveName: drive.name }); }
        } catch (e) { }
      }
      setFiles(allFiles);
      if (allFiles.length === 0) setSearchError("No .xlsx files found.");
    } catch (e) { setSearchError(e.message); }
    finally { setSearching(false); }
  }

  function handleSave() {
    const sp = {
      tenant,
      site,
      driveId: selected ? selected.driveId : manualDriveId,
      statusFileId: selected ? selected.id : manualFileId,
      statusFileName: selected ? selected.name : (project.sharepoint?.statusFileName || ""),
      summitDataFolder: project.sharepoint?.summitDataFolder || "",
    };
    onSave(sp);
  }

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Tenant</div>
          <input value={tenant} onChange={function(e) { setTenant(e.target.value); }}
            style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
        </div>
        <div>
          <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Site</div>
          <input value={site} onChange={function(e) { setSite(e.target.value); }}
            style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
        </div>
      </div>
      <button onClick={findFiles} disabled={searching}
        style={{ padding: "7px 14px", background: C.dark, color: C.gold, border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, fontWeight: 700, marginBottom: 10 }}>
        {searching ? "Searching..." : "🔍 Find Excel Files"}
      </button>
      {searchError && <div style={{ fontSize: 11, color: C.red, marginBottom: 8 }}>{searchError}</div>}
      {selected && <div style={{ fontSize: 12, color: C.green, fontWeight: 600, marginBottom: 8 }}>✓ {selected.name}</div>}
      {files.length > 0 && (
        <div>
          <input placeholder={"Filter " + files.length + " files..."}
            onChange={function(e) { setFileFilter(e.target.value.toLowerCase()); }}
            style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, marginBottom: 6, boxSizing: "border-box" }} />
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {files.filter(function(f) {
              return !fileFilter || f.name.toLowerCase().includes(fileFilter) || (f.parentReference?.path || "").toLowerCase().includes(fileFilter);
            }).map(function(file) {
              const sel = selected?.id === file.id;
              const folder = file.parentReference?.path ? (file.parentReference.path.split("root:")[1] || "/") : "/";
              return (
                <div key={file.id} onClick={function() { setSelected(file); }}
                  style={{ padding: "8px 10px", border: "1px solid " + (sel ? C.green : C.border), borderRadius: 4, marginBottom: 4, cursor: "pointer", background: sel ? "#f0fdf4" : C.white }}>
                  <div style={{ fontSize: 12, fontWeight: sel ? 700 : 400 }}>📊 {file.name}</div>
                  <div style={{ fontSize: 10, color: C.muted }}>{file.driveName} · {folder}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {files.length === 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>Drive ID (manual)</div>
            <input value={manualDriveId} onChange={function(e) { setManualDriveId(e.target.value); }}
              placeholder="b!lQhae..." style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>File ID (manual)</div>
            <input value={manualFileId} onChange={function(e) { setManualFileId(e.target.value); }}
              placeholder="0137WX5P..." style={{ width: "100%", padding: "6px 8px", border: "1px solid " + C.border, borderRadius: 4, fontSize: 12, boxSizing: "border-box" }} />
          </div>
        </div>
      )}
      <button onClick={handleSave} disabled={!selected && !manualDriveId}
        style={{ padding: "7px 16px", background: C.green, color: C.white, border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
        Save SharePoint Connection
      </button>
    </div>
  );
}

// ─── PROJECT DETAIL ───────────────────────────────────────────────────────────

function ProjectDetail({ project, onBack, onUpdate }) {
  const { instance, accounts } = useMsal();
  const [editing, setEditing] = useState(false);
  const [editingSP, setEditingSP] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTabConfigId, setActiveTabConfigId] = useState(null);
  const [newTabConfigId, setNewTabConfigId] = useState("");
  const [form, setForm] = useState({
    name: project.name, client: project.client,
    subtitle: project.subtitle || "", domain: project.domain || "",
    status: project.status || "active",
  });

  // Project writes are TrapperKeeper-TCC-Admin-only on the server.
  const authFetch = makeAuthFetch(instance, accounts[0]);

  async function handleSave() {
    setSaving(true);
    try {
      const res = await authFetch("/api/projects/" + project.id, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const updated = await res.json();
      if (!res.ok) throw new Error(updated.error || ("HTTP " + res.status));
      onUpdate(updated);
      setEditing(false);
    } catch (e) { alert("Save failed: " + e.message); }
    finally { setSaving(false); }
  }

  async function handleArchive() {
    if (!window.confirm("Archive this project?")) return;
    try {
      const res = await authFetch("/api/projects/" + project.id, { method: "DELETE" });
      if (!res.ok) throw new Error("HTTP " + res.status);
      onBack();
    } catch (e) { alert("Archive failed: " + e.message); }
  }

  const tabs = [...(project.tabs?.public || []), ...(project.tabs?.authenticated || [])];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={onBack} style={{ padding: "6px 12px", border: "1px solid " + C.border, borderRadius: 6, background: C.white, color: C.muted, cursor: "pointer", fontSize: 12 }}>← Back</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.dark }}>{project.name}</div>
          <div style={{ fontSize: 12, color: C.muted }}>{project.client}</div>
        </div>
        <Badge label={project.status || "active"} color={project.status || "active"} />
      </div>

      {editing ? (
        <Card>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            {[["name","Project Name"],["client","Client"],["subtitle","Subtitle"],["domain","Domain"]].map(function([field, label]) {
              return (
                <div key={field}>
                  <div style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>{label}</div>
                  <input value={form[field] || ""} onChange={function(e) { setForm(function(p) { return { ...p, [field]: e.target.value }; }); }}
                    style={{ width: "100%", padding: "7px 10px", border: "1px solid " + C.border, borderRadius: 6, fontSize: 12, boxSizing: "border-box" }} />
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleSave} disabled={saving} style={{ padding: "7px 18px", background: C.dark, color: C.gold, border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 700 }}>{saving ? "Saving..." : "Save"}</button>
            <button onClick={function() { setEditing(false); }} style={{ padding: "7px 14px", background: C.white, color: C.muted, border: "1px solid " + C.border, borderRadius: 6, cursor: "pointer", fontSize: 12 }}>Cancel</button>
          </div>
        </Card>
      ) : (
        <Card>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 12 }}>
            <div><div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>Domain</div><div style={{ fontSize: 12 }}>{project.domain || "—"}</div></div>
            <div><div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>Created</div><div style={{ fontSize: 12 }}>{project.createdAt || "—"}</div></div>
            <div><div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>Updated</div><div style={{ fontSize: 12 }}>{project.updatedAt || "—"}</div></div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={function() { setEditing(true); }} style={{ padding: "6px 14px", background: C.white, color: C.dark, border: "1px solid " + C.border, borderRadius: 6, cursor: "pointer", fontSize: 12 }}>Edit</button>
            <button onClick={handleArchive} style={{ padding: "6px 14px", background: C.white, color: C.red, border: "1px solid #fecaca", borderRadius: 6, cursor: "pointer", fontSize: 12 }}>Archive</button>
          </div>
        </Card>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.dark }}>SharePoint</div>
            <button onClick={function() { setEditingSP(!editingSP); }}
              style={{ fontSize: 11, padding: "3px 10px", border: "1px solid " + C.border, borderRadius: 4, cursor: "pointer", background: C.white, color: C.dark }}>
              {editingSP ? "Cancel" : "Change File"}
            </button>
          </div>
          {!editingSP ? (
            <>
              {project.sharepoint?.statusFileName
                ? <div style={{ fontSize: 12, color: C.green, fontWeight: 600, marginBottom: 6 }}>✓ {project.sharepoint.statusFileName}</div>
                : <div style={{ fontSize: 12, color: C.orange, marginBottom: 6 }}>⚠ No file selected</div>}
              {[["Tenant", project.sharepoint?.tenant], ["Site", project.sharepoint?.site]].map(function([label, val]) {
                return (
                  <div key={label} style={{ marginBottom: 4 }}>
                    <div style={{ fontSize: 10, color: C.muted }}>{label}</div>
                    <div style={{ fontSize: 11, color: val ? C.dark : C.muted }}>{val || "Not configured"}</div>
                  </div>
                );
              })}
            </>
          ) : (
            <SharePointEditor project={project} onSave={function(sp) {
              authFetch("/api/projects/" + project.id, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ sharepoint: sp }),
              })
              .then(function(r) { if (!r.ok) throw new Error("HTTP " + r.status); return r.json(); })
              .then(function(updated) { onUpdate(updated); setEditingSP(false); })
              .catch(function(e) { alert("Save failed: " + e.message); });
            }} />
          )}
        </Card>
        <Card>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.dark, marginBottom: 10 }}>Team ({Object.keys(project.team || {}).length})</div>
          {Object.entries(project.team || {}).map(function([name, email]) {
            const level = project.permissions?.userOverrides?.[email.toLowerCase()] || "write";
            return (
              <div key={name} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 12 }}>
                <span style={{ color: C.dark }}>{name}</span>
                <span style={{ color: LEVEL_COLORS[level] || C.muted, fontWeight: 600, fontSize: 11 }}>{level}</span>
              </div>
            );
          })}
        </Card>
        <Card style={{ gridColumn: "1 / -1" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.dark, marginBottom: 10 }}>Tabs ({tabs.length})</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {tabs.map(function(tab) {
              const isPublic = (project.tabs?.public || []).includes(tab);
              return (
                <span key={tab} style={{ padding: "3px 10px", borderRadius: 12, background: isPublic ? "#d1fae5" : "#f3f4f6", color: isPublic ? "#065f46" : C.muted, fontSize: 11 }}>
                  {tab}{isPublic ? " (public)" : ""}
                </span>
              );
            })}
          </div>
        </Card>
      <Card style={{ gridColumn: "1 / -1" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.dark }}>Tab Configs ({Object.keys(project.tabConfigs || {}).length})</div>
          <div style={{ display: "flex", gap: 6 }}>
            <input value={newTabConfigId} onChange={function(e) { setNewTabConfigId(e.target.value); }} placeholder="new-tab-id" style={{ fontSize: 11, padding: "5px 8px", border: "1px solid " + C.border, borderRadius: 6, width: 140 }} />
            <button onClick={function() { if (newTabConfigId.trim()) { setActiveTabConfigId(newTabConfigId.trim()); setNewTabConfigId(""); } }} style={{ padding: "5px 12px", background: C.dark, color: C.gold, border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>New</button>
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {Object.keys(project.tabConfigs || {}).length === 0 && (
            <div style={{ fontSize: 11, color: C.muted }}>No tab configs yet. Enter an id above to create one.</div>
          )}
          {Object.keys(project.tabConfigs || {}).map(function(id) {
            return (
              <button key={id} onClick={function() { setActiveTabConfigId(id); }} style={{ padding: "4px 10px", borderRadius: 12, background: C.light, border: "1px solid " + C.border, color: C.dark, fontSize: 11, cursor: "pointer" }}>
                {id}
              </button>
            );
          })}
        </div>
      </Card>
      {activeTabConfigId && (
        <TabConfigEditor
          project={project}
          authFetch={authFetch}
          tabId={activeTabConfigId}
          onClose={function() { setActiveTabConfigId(null); }}
          onSaved={function(updated) { onUpdate(updated); setActiveTabConfigId(null); }}
        />
      )}
      </div>
    </div>
  );
}

// ─── MAIN ADMIN TAB ───────────────────────────────────────────────────────────

export default function AdminTab() {
  const { instance, accounts } = useMsal();
  const { orgUsers } = useOrgDirectory();
  const [isAdmin, setIsAdmin] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [error, setError] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null); // null | "syncing" | "synced" | "error"
  const [syncError, setSyncError] = useState("");
  const [pendingSync, setPendingSync] = useState({ pendingCount: 0, pending: {}, log: [] });

  const userEmail = accounts[0]?.username || "";
  const account = accounts[0];
  const authFetch = useMemo(function() { return makeAuthFetch(instance, account); }, [instance, account]);

  // notify-server answers this only for TrapperKeeper-TCC-Admin tokens; a
  // non-admin's 403 means "not an admin" here.
  useEffect(function() {
    if (!userEmail) return;
    authFetch("/api/admin/acl?email=" + encodeURIComponent(userEmail))
      .then(function(r) { return r.ok ? r.json() : { isAdmin: false }; })
      .then(function(d) { setIsAdmin(!!d.isAdmin); })
      .catch(function() { setIsAdmin(false); });
  }, [userEmail, authFetch]);

  const loadProjects = useCallback(function() {
    setLoading(true);
    fetch("/api/projects")
      .then(function(r) { return r.json(); })
      .then(function(d) { setProjects(d.projects || []); setLoading(false); })
      .catch(function(e) { setError(e.message); setLoading(false); });
  }, []);

  useEffect(function() { loadProjects(); }, [loadProjects]);

  // Admin-only on the server; waiting for isAdmin also keeps this token
  // request from racing the ACL check's on mount.
  useEffect(function() {
    if (!isAdmin) return;
    authFetch("/api/notify/sync-status")
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { if (d) setPendingSync(d); })
      .catch(function() { /* leave default state if this fails */ });
  }, [syncStatus, isAdmin, authFetch]);

  async function handleSyncDirectory() {
    setSyncStatus("syncing");
    try {
      const tokenResp = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
      const res = await fetch("/api/notify/directory-sync", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + tokenResp.accessToken },
        body: JSON.stringify({ orgUsers: orgUsers }),
      });
      if (!res.ok) {
        const err = await res.json().catch(function() { return {}; });
        throw new Error(err.error || ("Request failed: " + res.status));
      }
      setSyncStatus("synced");
    } catch (err) {
      setSyncStatus("error");
      setSyncError(err.message);
    }
  }

  if (isAdmin === null) return <div style={{ padding: 40, textAlign: "center", color: C.muted }}>Checking access...</div>;

  if (!isAdmin) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.dark, marginBottom: 6 }}>Admin Access Required</div>
        <div style={{ fontSize: 13, color: C.muted }}>Your account ({userEmail}) does not have admin access. Contact Dan Frasco to request access.</div>
      </div>
    );
  }

  if (showWizard) {
    return (
      <ProjectWizard
        onSave={function(proj) { setShowWizard(false); loadProjects(); setSelectedProject(proj); }}
        onCancel={function() { setShowWizard(false); }}
      />
    );
  }

  if (selectedProject) {
    return (
      <ProjectDetail
        project={selectedProject}
        onBack={function() { setSelectedProject(null); loadProjects(); }}
        onUpdate={function(updated) { setSelectedProject(updated); loadProjects(); }}
      />
    );
  }

  const activeProjects = projects.filter(function(p) { return p.status !== "archived"; });
  const archivedProjects = projects.filter(function(p) { return p.status === "archived"; });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.dark, textTransform: "uppercase", letterSpacing: 1 }}>TrapperKeeper Admin</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Signed in as {userEmail} · {activeProjects.length} active project{activeProjects.length !== 1 ? "s" : ""}</div>
        </div>
        <button onClick={function() { setShowWizard(true); }}
          style={{ padding: "8px 18px", background: C.dark, color: C.gold, border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 700 }}>
          + New Project
        </button>
      </div>

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: C.red }}>
          Error loading projects: {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: C.muted }}>Loading...</div>
      ) : (
        <div>
          {activeProjects.length === 0 && (
            <div style={{ padding: 40, textAlign: "center", color: C.muted }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>📁</div>
              <div>No projects yet.</div>
            </div>
          )}

          {activeProjects.map(function(project) {
            return (
              <Card key={project.id} style={{ cursor: "pointer" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}
                  onClick={function() {
                    // Must be the authenticated (full) project: without a token
                    // notify-server returns the public view, which has no
                    // tabConfigs/team, and TabConfigEditor's save would then
                    // overwrite every other tab config.
                    authFetch("/api/projects/" + project.id)
                      .then(function(r) { if (!r.ok) throw new Error("HTTP " + r.status); return r.json(); })
                      .then(setSelectedProject)
                      .catch(function(e) { setError(e.message); });
                  }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: C.dark }}>{project.name}</div>
                      <Badge label={project.status || "active"} color={project.status || "active"} />
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>{project.client}</div>
                    <div style={{ display: "flex", gap: 16, fontSize: 11, color: C.muted }}>
                      <span>{project.tabCount || 0} tabs</span>
                      <span>{project.teamCount || 0} members</span>
                      <span>{project.domain || "No domain"}</span>
                      <span>Updated {project.updatedAt || "—"}</span>
                    </div>
                  </div>
                  <div style={{ color: C.muted, fontSize: 18, marginLeft: 12 }}>›</div>
                </div>
              </Card>
            );
          })}

          {archivedProjects.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Archived ({archivedProjects.length})</div>
              {archivedProjects.map(function(project) {
                return (
                  <div key={project.id} style={{ padding: "8px 12px", background: "#f9fafb", border: "1px solid " + C.border, borderRadius: 6, marginBottom: 6, display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 12, color: C.muted }}>{project.name} · {project.client}</span>
                    <span style={{ fontSize: 11, color: C.muted }}>Archived {project.updatedAt}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 24, padding: "12px 16px", background: "#f8f7f4", border: "1px solid " + C.border, borderRadius: 8, fontSize: 11, color: C.muted }}>
        Admin ACL is configured via ADMIN_ACL in .env on the server. To add admins: update ADMIN_ACL and restart the notify service.
      </div>

      <Card style={{ marginTop: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.dark, marginBottom: 8 }}>Directory Sync</div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
          Pushes the current live org directory ({orgUsers.length} people) to the SMS roster. SMS eligibility lags this sync -- it does not update live.
        </div>
        <button onClick={handleSyncDirectory} disabled={syncStatus === "syncing"}
          style={{ padding: "8px 16px", background: C.dark, color: C.gold, border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 700 }}>
          {syncStatus === "syncing" ? "Syncing..." : "Sync Directory"}
        </button>
        {syncStatus === "synced" && <span style={{ marginLeft: 10, color: C.green, fontSize: 12 }}>Synced.</span>}
        {syncStatus === "error" && <span style={{ marginLeft: 10, color: C.red, fontSize: 12 }}>Error: {syncError}</span>}
        {pendingSync.pendingCount > 0 && (
          <div style={{ marginTop: 12, background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 6, padding: 10, fontSize: 12, color: "#78350f" }}>
            {pendingSync.pendingCount} consent record(s) pending SharePoint audit sync -- will retry automatically on the next authenticated request.
          </div>
        )}
      </Card>
    </div>
  );
}
